import React from "react";
import { Edit3, ChevronDown } from "lucide-react";
import Dropdown from "../../../components/Dropdown";
import { TextField, DropdownField, SpinnerField, TextareaField } from "./FormFields";
import styles from "../css/inventoryItemDetail.module.css";
import { useAuth } from "../../../context/AuthContext";

export const DispensaryLayout = ({
  data,
  qty,
  setQty,
  reorder,
  setReorder,
  packSize,
  setPackSize,
  icdDisplay,
  setShowIcdModal,
  expiryDisplay,
  hasMultipleExpiry,
  setShowExpiryModal,
  category,
  setCategory,
  customCategory,
  setCustomCategory,
  categoryOptions,
  binValue,
  onChangeBinNumber,
  onChangeInvoiceNumber,
  onChangeCostPrice,
  onChangeNote,
  readOnly = false,
}) => {
  const { user } = useAuth();
  const rawSku = String(data?.sku ?? "");
  const bc = user?.branchcode ? String(user.branchcode).trim() : "";
  const skuDisplay = bc && !rawSku.startsWith(`${bc} - `) ? `${bc} - ${rawSku}` : rawSku;

  // Helper: format yyyy-mm-dd to dd/mm/yyyy for UI display only
  const formatDateDMY = (yyyyMmDd) => {
    const s = String(yyyyMmDd || '').trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return s;
    return `${m[3]}/${m[2]}/${m[1]}`;
  };
  return (
    <>
      {/* Dispensary layout exactly as design */}
      {/* Row 1: Nappi Code, SKU, Active ingredient, Strength, Quantity */}
      <div className={styles.rowSix}>
        <TextField label="Nappi Code" value={data.nappi} readOnly />
        <TextField label="SKU" value={skuDisplay} readOnly />
        <TextField label="Active ingredient" value={data.ingredient} readOnly />
        <DropdownField
          label="Strength"
          options={["250mg", "500mg", "1g"]}
          value={data.strength}
          readOnly={readOnly}
        />
        <SpinnerField
          label="Quantity"
          value={qty}
          onChange={readOnly ? undefined : setQty}
          min={0}
          step={1}
          ariaLabel="Quantity"
          compact
          readOnly={readOnly}
        />
      </div>

      <hr className={styles.divider} />

      {/* Row 2: Schedule, Expiry Date, Pack size, Dosage Form, Reorder level, Cost Price */}
      <div className={styles.rowSeven}>
        <TextField label="Schedule" value={data.schedule} readOnly />
        <div className={styles.field}>
          <div className={styles.icdLabelRow}>
            <label>Expiry</label>
            {!readOnly && (
            <button
              type="button"
              className={styles.icdEditBtn}
              onClick={() => setShowExpiryModal(true)}
              aria-label="Edit Expiry"
            >
              <Edit3 size={16} />
            </button>
            )}
          </div>
          <div
            className={styles.icdDisplay}
            role="button"
            tabIndex={0}
            onClick={() => setShowExpiryModal(true)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowExpiryModal(true)}
            aria-label="Open Expiry editor"
          >
            <input
              type="text"
              value={expiryDisplay}
              readOnly
              className={`${styles.icdReadonlyInput} ${styles.expiryReadonlyInput}`}
              aria-label="Selected expiry dates"
              onClick={() => setShowExpiryModal(true)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowExpiryModal(true)}
            />
            {hasMultipleExpiry && (
              <span className={styles.icdCaret} aria-hidden>
                <ChevronDown size={16} />
              </span>
            )}
          </div>
        </div>
        <SpinnerField
          label="Pack size"
          value={packSize}
          onChange={readOnly ? undefined : setPackSize}
          min={0}
          step={1}
          ariaLabel="Pack size"
          compact
          readOnly={readOnly}
        />
        <DropdownField
          label="Dosage Form"
          options={["Tablets", "Capsules", "Pills", "Syrup", "Injection"]}
          value={data.dosageForm}
          readOnly={readOnly}
        />
        <SpinnerField
          label="Reorder level"
          value={reorder}
          onChange={readOnly ? undefined : setReorder}
          min={0}
          step={1}
          ariaLabel="Reorder level"
          compact
          readOnly={readOnly}
        />
        <TextField label="Cost Price" value={data.costPrice} onChange={readOnly ? undefined : (e) => onChangeCostPrice?.(e.target.value)} readOnly={readOnly} />
      </div>

      <hr className={styles.divider} />

      {/* Row 3: Bin Number, Last Reorder, Category */}
      <div className={styles.rowFive}>
        <DropdownField
          label="Bin Number"
          options={[
            "Clinic Storage",
            "Warehouse A, Shelf 101",
            "Warehouse A, Shelf 102",
            "Warehouse B, Shelf 15",
            "Fridge A, Shelf 1",
            "OTC Storage",
          ]}
          value={binValue ?? data.binNumber}
          onChange={readOnly ? undefined : (val) => onChangeBinNumber?.(val)}
          readOnly={readOnly}
        />
        <TextField label="Last Reorder" value={formatDateDMY(data.lastReorder)} readOnly />
        <div className={styles.field}>
          <label>Category</label>
          <div className={styles.categoryFieldRow}>
            <div className={styles.categorySelectWrap}>
              {readOnly ? (
                <input type="text" value={category} readOnly />
              ) : (
                <Dropdown
                  options={categoryOptions}
                  value={category}
                  onChange={(val) => {
                    setCategory(val);
                    if (val !== "Other") setCustomCategory("");
                  }}
                />
              )}
            </div>
            {!readOnly && category === "Other" && (
              <input
                className={`${styles.categoryOtherInput} ${styles.frontShopOtherInputNarrow}`}
                type="text"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                placeholder="Enter category"
              />
            )}
          </div>
        </div>
      </div>

      <hr className={styles.divider} />

      {/* Notes */}
      <div className={styles.row}>
        <TextareaField label="Note" value={data.note} full onChange={readOnly ? undefined : (e) => onChangeNote?.(e.target.value)} readOnly={readOnly} />
      </div>
    </>
  );
};
